import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { generateReceiptHeader, generateReceiptFooter, getCollegeInfo } from './receiptHeaderUtils';
import { ensureRupeeFont, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';
applyPlugin(jsPDF);

/**
 * Generate Hostel Fee Receipt
 * Types: Admission, Security, Monthly, Damage
 */
export const generateHostelReceipt = async (paymentData, receiptType = 'HOSTEL FEE RECEIPT', collegeInfo = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const margin = 14;
  
  // Register Unicode-capable font for ₹ rendering
  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';

  // Get college info - prefer parameter over localStorage
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
    console.warn('generateHostelReceipt: collegeInfo not passed, using localStorage fallback. This may cause tenant isolation issues.');
  }
  let yPos = await generateReceiptHeader(doc, collegeInfo, receiptType);

  // Receipt Number and Date
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Receipt No:', margin, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(paymentData.receipt_number || paymentData.receipt_no || 'N/A', margin + 28, yPos);
  
  doc.setFont(resolvedFont, 'bold');
  doc.text('Date:', 140, yPos);
  doc.setFont(resolvedFont, 'normal');
  doc.text(paymentData.payment_date ? formatDate(paymentData.payment_date) : formatDate(new Date()), 155, yPos);

  yPos += 12;

  // Student Information
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 51, 102);
  doc.text('Student Information', margin, yPos);
  doc.setTextColor(0, 0, 0);
  
  yPos += 2;
  doc.setDrawColor(0, 51, 102);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;

  const studentInfo = [
    ['Student Name', paymentData.student_name || 'N/A'],
    ['Admission Number', paymentData.admission_number || paymentData.admission_no || 'N/A'],
    ['Course', paymentData.course_name || paymentData.course || 'N/A'],
    ['Session', paymentData.session || 'N/A'],
    ['Room Number', paymentData.room_number || paymentData.room_no || 'N/A'],
    ['Hostel Block', paymentData.hostel_block || paymentData.block || 'N/A']
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: studentInfo,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 45, fontStyle: 'bold', textColor: [80, 80, 80] },
      1: { cellWidth: 135, fontStyle: 'normal' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Payment Details
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 51, 102);
  doc.text('Payment Details', margin, yPos);
  doc.setTextColor(0, 0, 0);
  
  yPos += 2;
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;

  const paymentDetails = [
    ['Fee Type', paymentData.fee_type || receiptType.replace(' RECEIPT', '')],
    ['Amount', `${CURRENCY} ${formatINR(paymentData.amount || paymentData.paid_amount || 0)}`],
    ['Payment Mode', paymentData.payment_mode || 'Cash'],
    ['Transaction ID', paymentData.transaction_id || paymentData.reference_number || 'N/A']
  ];

  // Add month for monthly fee
  if (paymentData.month || paymentData.fee_month) {
    paymentDetails.splice(1, 0, ['Month', paymentData.month || paymentData.fee_month]);
  }

  doc.autoTable({
    startY: yPos,
    head: [],
    body: paymentDetails,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2, font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 45, fontStyle: 'bold', textColor: [80, 80, 80] },
      1: { cellWidth: 135, fontStyle: 'normal' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // Amount in Words
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Amount in Words:', margin, yPos);
  doc.setFont(resolvedFont, 'normal');
  const amountInWords = numberToWords(paymentData.amount || paymentData.paid_amount || 0);
  doc.text(amountInWords + ' Only', margin + 38, yPos);

  // Generate footer
  generateReceiptFooter(doc, collegeInfo);

  // Save the PDF
  const fileName = `Hostel_Receipt_${paymentData.receipt_number || paymentData.receipt_no || Date.now()}.pdf`;
  doc.save(fileName);
  
  return doc;
};

// Helper function to convert number to words
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  if (num === 0) return 'Zero';
  if (num < 0) return 'Minus ' + numberToWords(-num);
  
  let words = '';
  
  if (Math.floor(num / 10000000) > 0) {
    words += numberToWords(Math.floor(num / 10000000)) + ' Crore ';
    num %= 10000000;
  }
  
  if (Math.floor(num / 100000) > 0) {
    words += numberToWords(Math.floor(num / 100000)) + ' Lakh ';
    num %= 100000;
  }
  
  if (Math.floor(num / 1000) > 0) {
    words += numberToWords(Math.floor(num / 1000)) + ' Thousand ';
    num %= 1000;
  }
  
  if (Math.floor(num / 100) > 0) {
    words += numberToWords(Math.floor(num / 100)) + ' Hundred ';
    num %= 100;
  }
  
  if (num > 0) {
    if (num < 20) {
      words += ones[num];
    } else {
      words += tens[Math.floor(num / 10)];
      if (num % 10 > 0) {
        words += ' ' + ones[num % 10];
      }
    }
  }
  
  return words.trim() + ' Rupees';
};

// Specific receipt types
export const generateHostelAdmissionReceipt = (paymentData) => generateHostelReceipt(paymentData, 'HOSTEL ADMISSION RECEIPT');
export const generateHostelSecurityReceipt = (paymentData) => generateHostelReceipt(paymentData, 'HOSTEL SECURITY DEPOSIT RECEIPT');
export const generateHostelMonthlyReceipt = (paymentData) => generateHostelReceipt(paymentData, 'HOSTEL MONTHLY FEE RECEIPT');
export const generateHostelDamageReceipt = (paymentData) => generateHostelReceipt(paymentData, 'HOSTEL DAMAGE FEE RECEIPT');

export default generateHostelReceipt;
